import CSDTable from "@cloudscape-design/components-themed/table";
import {
  Box,
  TextFilter,
  SpaceBetween,
  Header,
} from "@cloudscape-design/components-themed";
import { useGetNoticesQuery } from "../../features/api";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { formatDate } from "../../utils/format";
import { Link } from "react-router-dom";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { useEffect, useState } from "react";

export interface Notice {
  conditionName: string;
  conditionUseUsage: string;
  fromName: string;
  fromAddress: string;
  toAddress: string;
  ccAddress: string;
  bccAddress: string;
  subject: string;
  emailBody: string;
  availableParameter: string;
  updatedAt: string;
  updatedBy: string;
}

const colummDefinitions = [
  {
    id: "conditionName",
    header: LABELS.NOTICE_CONDITION,
    cell: (e: any) => (
      <Link to={"/notifications/" + e.id}>{e.conditionName}</Link>
    ),
  },
  {
    id: "conditionUseUsage",
    header: LABELS.NOTICE_USAGE,
    cell: (e: any) => e.conditionUseUsage,
  },
  {
    id: "from",
    header: LABELS.NOTICE_FROM,
    cell: (e: any) => '"' + e.fromName + '" <' + e.fromAddress + ">",
  },
  {
    id: "toAddress",
    header: LABELS.NOTICE_TO,
    cell: (e: any) => e.toAddress,
  },
  {
    id: "ccAddress",
    header: LABELS.NOTICE_CC,
    cell: (e: any) => e.ccAddress,
  },
  {
    id: "bccAddress",
    header: LABELS.NOTICE_BCC,
    cell: (e: any) => e.bccAddress,
  },
  {
    id: "subject",
    header: LABELS.NOTICE_SUBJECT,
    cell: (e: any) => e.subject,
  },
  {
    id: "emailBody",
    header: LABELS.NOTICE_MAIL,
    cell: (e: any) => e.emailBody,
  },
  {
    id: "updatedAt",
    header: LABELS.NOTICE_LAST_UPDATED_DATE,
    cell: (e: any) => e.updateAtyyyyMMdd,
  },
  {
    id: "updatedBy",
    header: LABELS.NOTICE_LAST_UPDATED_BY,
    cell: (e: any) => e.updatedBy,
  },
];

/* 
通知マスタの一覧表
 */
const Table = () => {
  const { data, isFetching, refetch } = useGetNoticesQuery();

  // 更新日時の変換
  const updateData = data?.map((item) => ({
    ...item,
    updateAtyyyyMMdd: formatDate(item.updatedAt),
  })) as Notice[];

  /**ページャ関連 */
  const options = colummDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  const [filteringText, setFilteringText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Notice[]>([]);

  useEffect(() => {
    if (updateData !== undefined) {
      setPagedItems(getPagedItems(updateData, 1, DEFAULT_PAGE_SIZE));
      setAllItems(updateData);
      setFilteredItems(updateData);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(filteredItems.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**フィルタリング関数 */
  const handleFilteringTextOnChange = (value: string) => {
    setFilteringText(value);
    if (!value) {
      setFilteredItems(updateData);
      setPagedItems(getPagedItems(updateData, 1, preferences.pageSize));
      setCurrentPageIndex(1);
      return;
    }

    const newItems = updateData.filter(
      (item) =>
        (item.toAddress && item.toAddress.includes(value)) ||
        (item.ccAddress && item.ccAddress.includes(value)) ||
        (item.bccAddress && item.bccAddress.includes(value))
    );
    setFilteredItems(newItems);
    setPagedItems(getPagedItems(newItems, 1, preferences.pageSize));
    setCurrentPageIndex(1);
  };

  return (
    <CSDTable
      columnDefinitions={colummDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <Header>
            {LABELS.TABLE_TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder="TO、CC、BCCを検索"
          onChange={({ detail }) =>
            handleFilteringTextOnChange(detail.filteringText)
          }
          countText={`${filteredItems.length} 一致`}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={filteredItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
